'use client'

import * as React from 'react'
import { useCallback, useEffect } from 'react'
import confetti from 'canvas-confetti'

import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, NumberFlow } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { RewardStar } from '../components/reward-star'

export type CompleteLessonStatus = 'perfect' | 'success' | 'failed' | 'waiting'

export interface CompleteLessonModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Drives the title text and badge color. */
  status: CompleteLessonStatus
  /** Drives the RewardStar visual (0 = all empty/sleep). Independent of status. */
  stars: 0 | 1 | 2 | 3
  /** Name of the assignment / challenge / practice shown as subtitle. */
  title: string
  /** X in "X dari Y aktivitas selesai". */
  completedCount: number
  /** Y in "X dari Y aktivitas selesai". */
  totalCount: number
  /** Points number shown in the badge. Ignored visually when status='waiting' (shows ??? instead). */
  totalPoints: number
  /** Fire confetti when modal opens. Defaults to true when status is 'perfect' or 'success'. */
  confettiOnOpen?: boolean
}

const STATUS_CONFIG: Record<CompleteLessonStatus, { label: string }> = {
  perfect: {
    label: 'Sempurna!',
  },
  success: {
    label: 'Sukses selesai!',
  },
  failed: {
    label: 'Gagal selesai...',
  },
  waiting: {
    label: 'Menunggu nilai...',
  },
}

export function CompleteLessonModal({
  open,
  onOpenChange,
  status,
  stars,
  title,
  completedCount,
  totalCount,
  totalPoints,
  confettiOnOpen: confettiOnOpenProp,
}: CompleteLessonModalProps) {
  const config = STATUS_CONFIG[status]
  const confettiOnOpen = confettiOnOpenProp ?? status === 'perfect'

  const fireConfetti = useCallback(() => {
    const count = 200
    const defaults = {
      origin: { y: 0.7 },
      spread: 360,
      ticks: 100,
      gravity: 0,
      decay: 0.94,
      startVelocity: 30,
      zIndex: 2502,
    }

    function fire(particleRatio: number, opts: confetti.Options) {
      void confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      })
    }

    fire(0.25, { spread: 26, startVelocity: 55 })
    fire(0.2, { spread: 60 })
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 })
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 })
    fire(0.1, { spread: 120, startVelocity: 45 })
  }, [])

  useEffect(() => {
    if (open && confettiOnOpen) {
      fireConfetti()
    }
  }, [open, confettiOnOpen, fireConfetti])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'srd:w-full srd:max-w-[560px] srd:bg-neutrals-white',
          'srd:flex srd:flex-col srd:items-center srd:gap-y-4 srd:p-6',
        )}
        showCloseButton={false}
      >
        <DialogTitle className='srd:sr-only'>Hasil Belajar</DialogTitle>
        <DialogDescription className='srd:sr-only'>Hasil Belajar</DialogDescription>

        {/* Stars */}
        <RewardStar stars={stars} className='srd:max-w-[212px] srd:mt-4' />

        {/* Status badge */}
        <p className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:text-center srd:w-full')}>
          {config.label}
        </p>

        {/* Title + activity count */}
        <div className='srd:flex srd:flex-col srd:items-center srd:text-center srd:w-full'>
          <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>{title}</p>
          <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
            {completedCount} dari {totalCount} aktivitas selesai
          </p>
        </div>

        {/* Points badge */}
        <div className={cn('srd:flex srd:flex-col srd:items-center srd:text-center srd:gap-y-2')}>
          <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-grey-60')}>Total poin diraih</p>

          {status === 'waiting' ? (
            <p className={cn(typographyVariants({ variant: 'display' }), 'srd:text-neutrals-black')}>???</p>
          ) : (
            <NumberFlow
              value={totalPoints}
              className={cn(typographyVariants({ variant: 'display' }), 'srd:text-neutrals-black')}
              format={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
            />
          )}
        </div>

        {/* Close button */}
        <Button variant='primary' className='srd:w-fit' onClick={() => onOpenChange(false)}>
          Tutup
        </Button>
      </DialogContent>
    </Dialog>
  )
}
